/**
 * Dashboard section <-> URL path mapping.
 *
 * Each sidebar section gets its own path so it can be bookmarked, shared,
 * reloaded and reached with the browser's back/forward buttons. This is a
 * fixed table driven by the History API rather than a router dependency:
 * there are only a handful of sections and none take parameters. Caddy's
 * `try_files {path} /index.html` (deploy/ansible/roles/app/templates/
 * Caddyfile.j2) and Vite's dev server already serve the SPA for any path
 * that isn't a file, so a deep link needs no server change.
 *
 * None of these may collide with the API's own paths (/healthz, /readyz,
 * /v1/*) or the signed-out screens App.tsx reads directly (/accept-invite,
 * /reset-password, /forgot-password).
 */
export type TabId = "overview" | "flows" | "test-call" | "flows-list" | "results" | "team" | "developer" | "developer-api";

const TAB_PATHS: Record<TabId, string> = {
  overview: "/",
  flows: "/flow-designer",
  "test-call": "/test-call",
  "flows-list": "/templates",
  results: "/results",
  team: "/team",
  developer: "/api-keys",
  "developer-api": "/api-docs",
};

export function pathForTab(tab: TabId): string {
  return TAB_PATHS[tab];
}

/** Unknown paths resolve to the overview; a trailing slash is ignored. */
export function tabFromPath(pathname: string): TabId {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = (Object.keys(TAB_PATHS) as TabId[]).find((tab) => TAB_PATHS[tab] === normalized);
  return match ?? "overview";
}

/**
 * In-app navigation for code outside the sidebar (e.g. "Place test call"
 * after saving a template): pushes the URL, then fires the same popstate
 * event back/forward would, which Dashboard already listens to.
 */
export function navigateTo(url: string): void {
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
